import { globalActions } from 'app/slice';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { io, Socket } from 'socket.io-client';

interface NotificationData {
  type: 'appointment' | 'referral';
  recipient: string;
  entityId: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  room?: string;
}

interface NotificationsSocketProps {
  roomName?: string;
  onNotification?: (data: NotificationData) => void;
  onError?: (error: string) => void;
  onRoomJoined?: (roomName: string) => void;
  onRoomLeft?: (roomName: string) => void;
  onAppointmentNotification?: () => void; // New prop for appointment notifications
  onReferralNotification?: () => void; // New prop for referral notifications
}

const NotificationsSocket: React.FC<NotificationsSocketProps> = ({ 
  roomName = 'notification',
  onNotification, 
  onError,
  onRoomJoined,
  onRoomLeft,
  onAppointmentNotification,
  onReferralNotification
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [currentRoom, setCurrentRoom] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useDispatch();

  const handleConnect = useCallback(() => {
    setIsConnected(true);
    setConnectionAttempts(0);
 
    
    // Join the room after connection
    if (socketRef.current && roomName) {
      socketRef.current.emit('join-room', { room: roomName });
 
    }
  }, [roomName]);

  const handleDisconnect = useCallback((reason: string) => {
    setIsConnected(false);
    setCurrentRoom(null);

    
    if (reason === 'io server disconnect') {
      // Server disconnected, try to reconnect
      if (socketRef.current) {
        socketRef.current.connect();
      }
    }
  }, []);

  const handleConnectError = useCallback((error: Error) => {
    setIsConnected(false);
    const errorMessage = `Connection failed: ${error.message}`;
   
    setConnectionAttempts(prev => prev + 1);
  
    onError?.(errorMessage);
  }, []); // Remove onError dependency to prevent recreation

  const handleMessage = useCallback((data: NotificationData) => {
   
 
    dispatch(globalActions.setSocketNotification(data));
    onNotification?.(data);
    
    // Trigger appointment refetch if it's an appointment notification
    if (data.type === 'appointment' && onAppointmentNotification) {
   
      onAppointmentNotification();
    }
    
    // Trigger referral refetch if it's a referral notification
    if (data.type === 'referral' && onReferralNotification) {
     
      onReferralNotification();
    }
  }, [dispatch]); // Remove callback dependencies to prevent recreation

  const handleError = useCallback((error: string) => {
   
    onError?.(error);
  }, []); // Remove onError dependency to prevent recreation

  const handleRoomJoined = useCallback((data: { room: string; success: boolean }) => {
    if (data.success) {
      setCurrentRoom(data.room);
  
      onRoomJoined?.(data.room);
    } else {
    
    }
  }, []); // Remove onRoomJoined dependency to prevent recreation

  const handleRoomLeft = useCallback((data: { room: string }) => {
    setCurrentRoom(null);
   
    onRoomLeft?.(data.room);
  }, []); // Remove onRoomLeft dependency to prevent recreation

  const handleNotificationsHistory = useCallback((data: { notifications: NotificationData[] }) => {
   

  }, []);

  // Function to fetch notifications from server
  const fetchNotifications = useCallback(async () => {
    if (!socketRef.current || !isConnected) return;

 

    
    socketRef.current.emit('fetch-notifications', { room: roomName });
  }, [roomName]); // Remove isConnected dependency to prevent recreation

  // Function to mark notification as read
  const markAsRead = useCallback((notificationId: string) => {
    if (!socketRef.current || !isConnected) return;

 
    socketRef.current.emit('mark-notification-read', { 
      notificationId, 
      room: roomName 
    });
  }, [roomName]); // Remove isConnected dependency to prevent recreation

  // Function to leave current room
  const leaveRoom = useCallback(() => {
    if (!socketRef.current || !isConnected || !currentRoom) return;


    socketRef.current.emit('leave-room', { room: currentRoom });
  }, []); // Remove dependencies to prevent recreation

  useEffect(() => {
    const token = localStorage.getItem('api_token');
    
    if (!token) {
      const errorMsg = 'Authentication token not found';
      handleError(errorMsg);
      return;
    }

    // Only create socket if we don't have one or if it's disconnected
    if (socketRef.current && socketRef.current.connected) {
      return;
    }

    // Clear any existing timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    // Try different connection strategies based on attempt count
    let socketUrl = 'https://api-loop-c.appening.xyz';
    let transportConfig = ['websocket', 'polling'];
    
    if (connectionAttempts > 0) {
      // Try polling first on retry
      transportConfig = ['polling', 'websocket'];
    }

    // Create socket with configuration
    const socketOptions: any = {
      transports: transportConfig,
      upgrade: true,
      rememberUpgrade: true,
      timeout: 20000,
      forceNew: true,
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 3,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    };

    // Only add auth if we have a token and haven't tried without it
    if (token && connectionAttempts <= 2) {
      socketOptions.auth = { token };
    }

    const socket = io(socketUrl, socketOptions);
    socketRef.current = socket;

    // Set up event listeners
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);
    socket.on('notification', handleMessage);
    socket.on('room-joined', handleRoomJoined);
    socket.on('room-left', handleRoomLeft);
    socket.on('notifications-history', handleNotificationsHistory);
    socket.on('error', handleError);

    // Cleanup function
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      // Leave room before disconnecting
      if (socket.connected && currentRoom) {
        socket.emit('leave-room', { room: currentRoom });
      }
      
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      socket.off('notification', handleMessage);
      socket.off('room-joined', handleRoomJoined);
      socket.off('room-left', handleRoomLeft);
      socket.off('notifications-history', handleNotificationsHistory);
      socket.off('error', handleError);
      
      if (socket.connected) {
        socket.disconnect();
      }
      
      socketRef.current = null;
    };
  }, [connectionAttempts, roomName]); // Removed callback dependencies to prevent recreation

  // Fetch notifications when connected and room is joined
  useEffect(() => {
    if (isConnected && currentRoom === roomName) {
      fetchNotifications();
    }
  }, [isConnected, currentRoom, roomName]); // Remove fetchNotifications dependency

  // Return null - no UI rendering
  return null;
};

export default NotificationsSocket;
